import Big from 'big.js'

import prettyAmount from './prettyAmount'

// The tip menu, shared by both donation widgets (v6's TippingSelect, v7's
// Tipping) so the two can't drift on how a raw tip-rate value becomes a
// label — and on how a zero is spelled, which the reducers need too.

// The value the zero entry carries for this row under this flag —
// undefined only when the row has no zero of its own AND the flag is off,
// which together mean no such entry gets rendered. Option values are
// per-row, so this is the one place that decides the spelling: a zero the
// config already carries keeps its own string (a row may write it '0.0',
// and its `default` index may point at it), and otherwise the injected
// option gets a plain '0'.
//
// `find` + an undefined check rather than testing the found value for
// truthiness, because a numeric 0 in the list is falsy and would lose the
// original string this exists to preserve.
//
// `|| []` because matrix rows come straight off the wire, where an
// options list can be absent or JSON null.
export const zeroTipValue = (options, zeroTip) => {
  const own = (options || []).find(option => Big(option).eq(0))
  if (own !== undefined) return own

  return zeroTip ? '0' : undefined
}

// A configured option is either a fraction of the donation (lt 1,
// rendered "<pct>%") or a fixed currency amount (gte 1, rendered as
// money). That's inferred from magnitude alone, which leaves 1 as the
// ceiling for percentages and $1 as the floor for fixed amounts — a
// known limitation of the wire format, not something to work around
// here. Money labels mirror calcTip's rounding so the label matches the
// tip the donor is actually charged.
//
// Zero is the exception, because $0 and 0% are the same tip and its own
// magnitude can't say which form to use: it borrows the unit of the row
// it sits in. Any fixed amount among that row's rates and the zero
// renders as money, otherwise as a percentage — so it never reads as "0%"
// beside dollar-denominated siblings, and never as "$0" beside
// percentages. Deliberately a quiet "$0"/"0%" rather than wording like
// "No tip": declining should be available without being advertised.
//
// `zeroTip` (PWC data.tip_rates.zero_option) injects that option rather
// than requiring it in every matrix row and fixed-options list, the same
// way the trailing "Other" entry is injected. It leads the list because
// the menu ascends.
const tipOptions = ({ options, zeroTip, i18n, currency }) => {
  // `|| []`, not a default parameter: a default only fires on undefined,
  // and v7 passes `tippingConfig?.options` straight through, so a JSON
  // null would turn "renders just Other" into a blank widget.
  const list = options || []
  // Hoisting is gated on zeroTip rather than on there being a zero: when
  // the flag is off, a zero a config wrote into its own list stays where
  // the config put it, exactly as it rendered before this option existed.
  // `true` rather than `zeroTip` because inside this branch it is one, and
  // that's what makes the undefined return unreachable here.
  const values = zeroTip
    ? [ zeroTipValue(list, true) ]
      .concat(list.filter(option => !Big(option).eq(0)))
    : list

  // Read off the configured rates because that's what "the row's unit"
  // means — though `values` would give the same answer for every input,
  // since it differs only by zeros and no zero satisfies gte(1). A row
  // with no rates at all (a broken config) has no fixed amounts either, so
  // its lone injected zero falls to the percentage side.
  const anyFixedAmount = list.some(option => Big(option).gte(1))

  return values.map(option => {
    const big = Big(option)
    // Magnitude decides for a real rate; the row decides for a zero.
    const asMoney = big.eq(0) ? anyFixedAmount : big.gte(1)

    return {
      value: option,
      label: asMoney
        // Mirrors calcTip's rounding, so the label matches the tip charged.
        ? i18n.formatCurrency(big.round(currency.exponent, 0), currency)
        : prettyAmount(big.times(100), 1) + '%',
    }
  })
  // Empty value = "Other", which toggles a free-form tip input.
  .concat({ value: '', label: i18n.t('other') })
}

// Which tip option to select when the donor's amount crosses a matrix
// threshold and a new row takes over. The row's own default normally
// wins: the donor's previous pick was a *rate* suggested by the old row
// (10% vs 15%), and the new row suggests its own.
//
// A declined tip is different in kind. The zero option is an opt-out, not a
// rate, so re-suggesting 15% because the donor edited their amount would
// quietly reverse a decision they made deliberately — and the older
// route to zero (Other → 0) already survives the transition untouched,
// since resetTipping doesn't clear otherTip/tipText. So an explicit
// decline carries across rows too.
//
// The carried value is resolved against the *incoming* row, not copied
// from the outgoing one, because rows spell zero differently: crossing
// from a row that wrote its own '0.0' into one that gets the injected '0'
// would otherwise leave state holding a value the new menu doesn't
// contain. No visible symptom today — a controlled <select> whose value
// matches no <option> falls back to the first one, and the hoist above
// makes that the zero — so this keeps state and menu in agreement rather
// than fixing a live defect. Don't "simplify" it back to copying the old
// value on the grounds that nothing breaks: what's holding that together
// is the zero being at index 0, which is the hoist's business, not this
// function's.
//
// Falls back to the row's default when the incoming row offers no zero at
// all — which is what `zeroTipValue` returning undefined means, and which
// takes the flag being off *and* that row carrying no zero of its own.
// Either one alone still yields a zero entry to carry, so a donor who
// declined via a row's own zero keeps the decline when the next row also
// carries one, flag or no flag.
//
// `declined` is tracked in reducer state rather than inferred from the
// current value, because a row whose *default* is zero (a matrix that
// suggests no tip for small gifts) is the partner's suggestion, not the
// donor's decision, and shouldn't stick to rows that suggest a rate.
export const nextTipOption = ({ declined, config, zeroTip }) => {
  if (!declined) return config.default

  // Explicit undefined check: a numeric 0 is a valid carried value here.
  const zero = zeroTipValue(config.options, zeroTip)

  return zero === undefined ? config.default : zero
}

export default tipOptions
